import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { MatTableModule, MatTableDataSource } from '@angular/material/table';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { ProcurementService } from '../_services/procurement.service';
import { EmployeeService } from '../_services/employee.service';
import { ToastrService } from 'ngx-toastr';
import { UtilsService } from '../_services/utils.service';
import { ProcurementGroup } from '../_model/procurement/ProcurementGroup';
import { PoItemAuthorityDto } from '../_model/procurement/PoItemAuthority';

/** Sol/sağ listelerde satır olarak kullanılan yetki (görüntüleme için açıklamalı) */
export interface AuthorityRow extends PoItemAuthorityDto {
  pgCodeDesc?: string;
  pgdCodeDesc?: string;
  wgCodeDesc?: string;
}

@Component({
  selector: 'app-item-authorizations',
  templateUrl: './item-authorizations.component.html',
  styleUrls: ['./item-authorizations.component.sass'],
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatFormFieldModule,
    MatSelectModule,
    MatInputModule,
    MatButtonModule,
    MatTableModule,
    MatCheckboxModule
  ]
})
export class ItemAuthorizationsComponent implements OnInit {
  employees: { id: number; fullName?: string; name?: string; surname?: string }[] = [];
  selectedEmployeeId: number | null = null;

  /** Sol liste: Çalışana atanmış yetkiler */
  currentAuthorities: AuthorityRow[] = [];
  leftFilter = '';
  leftDataSource: MatTableDataSource<AuthorityRow>;
  leftDisplayedColumns = ['select', 'pgCode', 'pgdCode', 'wgCode', 'description'];

  /** Sağ liste: Tüm tanımlı gruplar (PG/PGD/WG flatten) */
  allAuthorityOptions: AuthorityRow[] = [];
  rightFilter = '';
  rightDataSource: MatTableDataSource<AuthorityRow>;
  rightDisplayedColumns = ['select', 'pgCode', 'pgdCode', 'wgCode', 'description'];

  leftSelection = new Set<string>();
  rightSelection = new Set<string>();

  loading = false;
  saving = false;

  constructor(
    private procurementService: ProcurementService,
    private employeeService: EmployeeService,
    private toastr: ToastrService,
    private utilsService: UtilsService
  ) {
    this.leftDataSource = new MatTableDataSource<AuthorityRow>([]);
    this.rightDataSource = new MatTableDataSource<AuthorityRow>([]);
  }

  ngOnInit(): void {
    this.setupFilterPredicates();
    this.loadEmployees();
    this.loadAllAuthorityOptions();
  }

  private setupFilterPredicates(): void {
    this.leftDataSource.filterPredicate = (data: AuthorityRow, filter: string) =>
      !filter || this.utilsService.toTurkishLowerCase(this.rowSearchText(data)).includes(filter);
    this.rightDataSource.filterPredicate = (data: AuthorityRow, filter: string) =>
      !filter || this.utilsService.toTurkishLowerCase(this.rowSearchText(data)).includes(filter);
  }

  private rowSearchText(row: AuthorityRow): string {
    const parts = [
      row.pgCode,
      row.pgdCode,
      row.wgCode ?? '',
      row.pgCodeDesc ?? '',
      row.pgdCodeDesc ?? '',
      row.wgCodeDesc ?? ''
    ];
    return parts.join(' ');
  }

  private rowKey(row: PoItemAuthorityDto): string {
    return `${row.pgCode}|${row.pgdCode}|${row.wgCode ?? ''}`;
  }

  loadEmployees(): void {
    this.employeeService.getEmployeeList(-1).subscribe({
      next: (result) => {
        const items = result?.responseData?.items ?? [];
        this.employees = items
          .filter((e: any) => e?.id != null)
          .map((e: any) => ({
            id: e.id,
            fullName: e.fullName ?? [e.name, e.surname].filter(Boolean).join(' ')
          }))
          .sort((a: any, b: any) =>
            (a.fullName || '').toLowerCase().localeCompare((b.fullName || '').toLowerCase(), 'tr')
          );
      },
      error: (err) => {
        this.toastr.error(err?.error?.msg || 'Çalışan listesi yüklenemedi.', 'Hata');
      }
    });
  }

  /** getProcurementGroups ağacını PG/PGD/WG satırlarına düzleştirir */
  loadAllAuthorityOptions(): void {
    this.procurementService.getProcurementGroups().subscribe({
      next: (response) => {
        if (!response?.success || !response?.responseData?.items) {
          this.allAuthorityOptions = [];
          this.rightDataSource.data = [];
          return;
        }
        const flattened: AuthorityRow[] = [];
        const pgList = response.responseData.items as ProcurementGroup[];
        for (const pg of pgList) {
          const pgdList = pg.children ?? [];
          if (pgdList.length === 0) {
            flattened.push({
              pgCode: pg.value ?? pg.code,
              pgdCode: '',
              wgCode: null,
              pgCodeDesc: pg.description,
              pgdCodeDesc: '',
              wgCodeDesc: ''
            });
          } else {
            for (const pgd of pgdList) {
              const wgList = pgd.children ?? [];
              if (wgList.length === 0) {
                flattened.push({
                  pgCode: pg.value ?? pg.code,
                  pgdCode: pgd.value ?? pgd.code,
                  wgCode: null,
                  pgCodeDesc: pg.description,
                  pgdCodeDesc: pgd.description,
                  wgCodeDesc: ''
                });
              } else {
                for (const wg of wgList) {
                  flattened.push({
                    pgCode: pg.value ?? pg.code,
                    pgdCode: pgd.value ?? pgd.code,
                    wgCode: wg.value ?? wg.code ?? null,
                    pgCodeDesc: pg.description,
                    pgdCodeDesc: pgd.description,
                    wgCodeDesc: wg.description ?? ''
                  });
                }
              }
            }
          }
        }
        this.allAuthorityOptions = flattened;
        this.rightDataSource.data = [...flattened];
      },
      error: (err) => {
        this.toastr.error(err?.error?.msg || 'Grup listesi yüklenemedi.', 'Hata');
      }
    });
  }

  onEmployeeChange(): void {
    this.leftSelection.clear();
    this.rightSelection.clear();
    if (this.selectedEmployeeId == null) {
      this.currentAuthorities = [];
      this.leftDataSource.data = [];
      return;
    }
    this.loadCurrentAuthorities();
  }

  loadCurrentAuthorities(): void {
    if (this.selectedEmployeeId == null) return;
    this.loading = true;
    this.procurementService.getPoItemAuthorities(this.selectedEmployeeId).subscribe({
      next: (response) => {
        this.loading = false;
        const items = response?.responseData?.items ?? [];
        this.currentAuthorities = items.map((a: PoItemAuthorityDto) => ({
          ...a,
          pgCodeDesc: '',
          pgdCodeDesc: '',
          wgCodeDesc: ''
        }));
        this.enrichCurrentWithDescriptions();
        this.leftDataSource.data = [...this.currentAuthorities];
      },
      error: (err) => {
        this.loading = false;
        this.toastr.error(err?.error?.msg || 'Yetkiler yüklenemedi.', 'Hata');
        this.currentAuthorities = [];
        this.leftDataSource.data = [];
      }
    });
  }

  /** Mevcut yetkilere PG/PGD/WG açıklamalarını eşleştir */
  private enrichCurrentWithDescriptions(): void {
    for (const row of this.currentAuthorities) {
      const pg = this.allAuthorityOptions.find(
        (o) => o.pgCode === row.pgCode && o.pgdCode === row.pgdCode && (o.wgCode ?? '') === (row.wgCode ?? '')
      );
      if (pg) {
        row.pgCodeDesc = pg.pgCodeDesc;
        row.pgdCodeDesc = pg.pgdCodeDesc;
        row.wgCodeDesc = pg.wgCodeDesc;
      }
    }
  }

  applyLeftFilter(): void {
    this.leftDataSource.filter = this.utilsService.toTurkishLowerCase((this.leftFilter || '').trim());
  }

  applyRightFilter(): void {
    this.rightDataSource.filter = this.utilsService.toTurkishLowerCase((this.rightFilter || '').trim());
  }

  toggleLeftSelect(row: AuthorityRow, checked: boolean): void {
    const key = this.rowKey(row);
    if (checked) this.leftSelection.add(key);
    else this.leftSelection.delete(key);
  }

  toggleRightSelect(row: AuthorityRow, checked: boolean): void {
    const key = this.rowKey(row);
    if (checked) this.rightSelection.add(key);
    else this.rightSelection.delete(key);
  }

  isLeftSelected(row: AuthorityRow): boolean {
    return this.leftSelection.has(this.rowKey(row));
  }

  isRightSelected(row: AuthorityRow): boolean {
    return this.rightSelection.has(this.rowKey(row));
  }

  /** Sağdaki seçilenleri sola ekle (zaten varsa ekleme) */
  addSelected(): void {
    const toAdd: AuthorityRow[] = [];
    this.rightDataSource.filteredData.forEach((row) => {
      if (this.rightSelection.has(this.rowKey(row))) {
        const exists = this.currentAuthorities.some((c) => this.rowKey(c) === this.rowKey(row));
        if (!exists) toAdd.push({ ...row });
      }
    });
    this.currentAuthorities = [...this.currentAuthorities, ...toAdd];
    this.leftDataSource.data = [...this.currentAuthorities];
    this.rightSelection.clear();
    this.toastr.info(`${toAdd.length} yetki eklendi.`, 'Bilgi');
  }

  /** Soldaki seçilenleri kaldır */
  removeSelected(): void {
    const keysToRemove = new Set(this.leftSelection);
    this.currentAuthorities = this.currentAuthorities.filter((r) => !keysToRemove.has(this.rowKey(r)));
    this.leftDataSource.data = [...this.currentAuthorities];
    this.leftSelection.clear();
  }

  save(): void {
    if (this.selectedEmployeeId == null) {
      this.toastr.warning('Önce bir çalışan seçin.', 'Uyarı');
      return;
    }
    this.saving = true;
    const authorities: PoItemAuthorityDto[] = this.currentAuthorities.map((r) => ({
      pgCode: r.pgCode,
      pgdCode: r.pgdCode,
      wgCode: r.wgCode ?? undefined
    }));
    this.procurementService
      .savePoItemAuthorities({
        employeeId: this.selectedEmployeeId,
        authorities
      })
      .subscribe({
        next: (res) => {
          this.saving = false;
          const msg = (res as any)?.responseData?.item ?? 'Yetkiler kaydedildi.';
          this.toastr.success(msg, 'Başarılı');
        },
        error: (err) => {
          this.saving = false;
          this.toastr.error(err?.error?.msg || 'Kayıt sırasında hata oluştu.', 'Hata');
        }
      });
  }

  getEmployeeDisplayName(emp: { fullName?: string; name?: string; surname?: string }): string {
    return emp?.fullName ?? [emp?.name, emp?.surname].filter(Boolean).join(' ') ?? '';
  }
}
